import React from "react";
import { Message } from "@/types";
import { Button } from "@/components/ui/button";
import {
  ThumbsUp,
  ThumbsDown,
  // VolumeX,
  // Volume2,
  Pencil,
  Copy,
  Share2,
  RefreshCw,
  // MoreVertical,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
// import { useVoiceChatStore } from "@/features/voice-chat";
// import { useChatStore } from "@/features/chat";
import { useChatAccessStore } from "@/features/chat/store/chat-access-store";
import { useCurrentChatId } from "@/hooks";
import { useToast } from "@/features/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MessageActionsProps {
  message: Message;
  onAction: (
    messageId: string,
    action: "like" | "dislike" | "speak" | "stop" | "simplify" | "edit"
  ) => void | Promise<void>;
}

const STROKE_WIDTH = 2;
const ICON_SIZE = "h-3.5 w-3.5 sm:h-4 sm:w-4";
export const MessageActions: React.FC<MessageActionsProps> = ({
  message,
  onAction,
}) => {
  const { t } = useTranslation();
  const { success } = useToast();
  // ✅ Voice chat artık store-based
  // const { isSpeaking } = useVoiceChatStore();
  const feedback =
    message.feedback === 1 ? "like" : message.feedback === 2 ? "dislike" : null;

  // Store'dan mesajları al
  // const { chats } = useChatStore();
  const currentChatId = useCurrentChatId();

  // Bu mesajın en son mesaj olup olmadığını kontrol et
  // const isLastMessage = React.useMemo(() => {
  //   if (!currentChatId) return false;

  //   const currentChat = chats.find((chat) => chat.id === currentChatId);
  //   if (!currentChat || currentChat.messages.length === 0) return false;

  //   const lastMessage = currentChat.messages[currentChat.messages.length - 1];
  //   return lastMessage.id === message.id;
  // }, [chats, message.id, currentChatId]);

  // 🔒 Shared read-only mod kontrolü
  const chatAccess = useChatAccessStore((state) =>
    currentChatId ? state.byChatId[currentChatId] : undefined
  );
  const isReadOnly = chatAccess?.canEdit === false;

  const handleFeedback = (type: "like" | "dislike") => {
    onAction(message.id, type);
  };

  const handleCopy = async () => {
    try {
      const content = message.content || "";
      if (!content.trim()) return;

      await navigator.clipboard.writeText(content);
      success(t("common.copiedToClipboard") || "Copied to clipboard");
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  const handleShare = async () => {
    try {
      const content = message.content || "";
      if (!content.trim()) return;

      if (navigator.share) {
        await navigator.share({
          title: t("chat.shareMessage"),
          text: content,
        });
      } else {
        await navigator.clipboard.writeText(content);
        success(t("common.copiedToClipboard") || "Copied to clipboard");
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  if (message.role !== "assistant") return null;
  // Model adı hover state'i üst parenttan alınacak (mesaj kutusu hover)
  // Bu componentin parent'ı (OptimizedMessage) üzerinden bir prop ile hover state'i alabiliriz.
  // Ancak burada doğrudan CSS ile parent hover'ını kullanacağız.

  // 🎨 Edit butonu sadece libraryItems varsa göster
  const hasGenerationContent =
    message.libraryItems && message.libraryItems.length > 0;

  // Read-only modda aksiyon barını hiç göstermiyoruz
  if (isReadOnly) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center justify-start gap-1 relative">
        <div className="flex items-center gap-x-1">
          {/* Copy Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
                onClick={handleCopy}
              >
                <Copy className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("common.copy")}
            </TooltipContent>
          </Tooltip>

          {/* Thumbs Up Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm",
                  feedback === "like" &&
                    "text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300"
                )}
                onClick={() => handleFeedback("like")}
              >
                <ThumbsUp className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("feedback.like")}
            </TooltipContent>
          </Tooltip>

          {/* Thumbs Down Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm",
                  feedback === "dislike" &&
                    "text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                )}
                onClick={() => handleFeedback("dislike")}
              >
                <ThumbsDown className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("feedback.dislike")}
            </TooltipContent>
          </Tooltip>

          {/* Share Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
                onClick={handleShare}
              >
                <Share2 className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("common.share")}
            </TooltipContent>
          </Tooltip>

          {/* Refresh Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
                onClick={() => onAction(message.id, "simplify")}
              >
                <RefreshCw className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("actions.regenerate")}
            </TooltipContent>
          </Tooltip>

          {/* Voice/Speak Button - Conditional */}
          {/* {!isSpeaking || !isLastMessage ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
                  onClick={() => onAction(message.id, "speak")}
                >
                  <Volume2 className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
              >
                {t("actions.speak")}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
                  onClick={() => onAction(message.id, "stop")}
                >
                  <VolumeX className={ICON_SIZE} strokeWidth={STROKE_WIDTH} />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
              >
                {t("chat.stopSpeaking")}
              </TooltipContent>
            </Tooltip>
          )} */}
          {/* More Actions Button */}
          {/* <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 sm:h-8 sm:w-8 text-foreground hover:bg-bg-tertiary dark:hover:bg-gray-700 rounded-sm"
              >
                <MoreVertical
                  className={ICON_SIZE}
                  strokeWidth={STROKE_WIDTH}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
            >
              {t("common.moreActions")}
            </TooltipContent>
          </Tooltip> */}
          {/* Edit Button - Only for generation messages */}
          {hasGenerationContent && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground dark:text-gray-400 dark:hover:text-gray-200"
                  onClick={() => onAction(message.id, "edit")}
                >
                  <Pencil className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={1.5} />
                </Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-black dark:bg-black text-white dark:text-white border-0 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg"
              >
                {t("common.edit")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {/* Simplify button - only for text messages */}
        {/* {message.content &&
        !message.images &&
        !message.audioUrl &&
        !message.generatedContent && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs text-muted-foreground hover:text-foreground border-border-secondary bg-bg-secondary hover:bg-bg-tertiary dark:border-border-primary dark:bg-bg-tertiary dark:hover:bg-bg-quaternary"
            onClick={() => onAction(message.id, "simplify")}
            title={t("actions.simplify")}
          >
            {t("actions.simplify")}
          </Button>
        )} */}
        {message.selectedModel && (
          <span className="hidden group-hover:inline text-xs font-semibold text-foreground">
            {message.selectedModel}
          </span>
        )}
      </div>
    </TooltipProvider>
  );
};
